import { SWRConfig } from 'swr';
import { useRef, useState, Suspense, useEffect, type ReactNode } from 'react';

import { Alert, Stack, Button } from '@mui/material';

import { AUTH_LOGOUT_REASON } from 'src/auth/store/firebase/logout-reason';
import { getCurrentAuthSession } from 'src/auth/store/firebase/auth-session';
import { invalidateSessionWork, registerSessionCleanup } from 'src/auth/session-lifecycle';
import useAuthStore, {
  waitForAuthReady,
  recoverAuthSession,
} from 'src/auth/store/firebase/auth-store';

import { SplashScreen } from 'src/components/loading-screen';

import { resetPointPayStores } from './reset-stores';
import { PointPaySessionContext } from './session-context';
import { type PointPayIdentity, resolvePointPayIdentity } from './session-identity';

export default function PointPaySessionBoundary({ children }: { children: ReactNode }) {
  const authenticated = useAuthStore((state) => state.authenticated);
  const initialized = useAuthStore((state) => state.authInitialized);
  const status = useAuthStore((state) => state.authRestorationStatus);
  const uid = useAuthStore((state) => state.user?.uid);
  const [ready, setReady] = useState(false);
  const [identity, setIdentity] = useState<PointPayIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const logoutRef = useRef<Promise<void> | null>(null);
  const scope = authenticated ? uid : null;
  const previousScope = useRef(scope);

  useEffect(() => {
    resetPointPayStores();
    return registerSessionCleanup(() => setIdentity(null));
  }, []);

  useEffect(() => {
    if (previousScope.current !== scope) {
      previousScope.current = scope;
      invalidateSessionWork();
    }
  }, [scope]);

  useEffect(() => {
    let active = true;
    waitForAuthReady().then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || !initialized) return undefined;
    let active = true;
    setIdentity(null);
    setError(null);
    if (!authenticated || status !== 'AUTHENTICATED') return undefined;

    const check = async () => {
      // Do this above both GuestGuard and AuthGuard: a restored legacy session
      // must not bounce from the login page back into protected routes.
      if (!uid?.startsWith('user:')) {
        if (!logoutRef.current) {
          logoutRef.current = useAuthStore
            .getState()
            .logout({ reason: AUTH_LOGOUT_REASON.USER_LOGOUT })
            .finally(() => {
              logoutRef.current = null;
            });
        }
        await logoutRef.current;
        return;
      }
      const session = await getCurrentAuthSession();
      if (!active) return;
      if (!session || session.uid !== uid)
        throw new Error('Your sign-in could not be verified. Reconnect and retry.');
      const verified = resolvePointPayIdentity(session);
      setIdentity(verified);
    };
    check().catch((failure) => {
      if (active)
        setError(failure instanceof Error ? failure.message : 'Unable to restore your sign-in.');
    });
    return () => {
      active = false;
    };
  }, [authenticated, initialized, ready, status, uid, attempt]);

  if (!ready || !initialized || status === 'RESTORING') return <SplashScreen />;
  if (status === 'RECOVERY_REQUIRED' || error) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 440, mx: 'auto', p: 3 }}>
        <Alert severity="warning">
          {error || 'Your saved sign-in could not be restored. Reconnect and retry.'}
        </Alert>
        <Button
          onClick={() => {
            recoverAuthSession()
              .catch(() => undefined)
              .finally(() => setAttempt((value) => value + 1));
          }}
        >
          Retry
        </Button>
        <Button
          onClick={() =>
            useAuthStore
              .getState()
              .logout({ reason: AUTH_LOGOUT_REASON.USER_LOGOUT })
              .catch(() => setError('Unable to finish signing out. Please retry.'))
          }
        >
          Log out
        </Button>
      </Stack>
    );
  }
  if (authenticated && (!identity || identity.uid !== uid)) return <SplashScreen />;
  return (
    <PointPaySessionContext.Provider value={authenticated ? identity : null}>
      <SWRConfig
        key={authenticated && identity ? `${identity.businessId}:${identity.uid}` : 'signed-out'}
        value={{ provider: () => new Map() }}
      >
        {/* Keep SWR's layout-effect lifecycle mounted while lazy routes suspend.
            Hiding the provider removes its internal state before consumers resume. */}
        <Suspense fallback={<SplashScreen />}>{children}</Suspense>
      </SWRConfig>
    </PointPaySessionContext.Provider>
  );
}
